import ApiService from './api.service'
import type {
  ProjectAccessResponse,
  ProjectAccessListResponse,
  UserInvitationListResponse,
  InvitationActionResponse,
  ProjectInviteData,
  ProjectAccessUpdateData
} from '@/types/services'

/**
 * Service for managing project access-related API endpoints
 */
class ProjectAccessService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Access Error'
      }
    })
  }

  /**
   * Get all clients with access to a project
   */
  async getProjectAccess(projectId: string): Promise<ProjectAccessListResponse | null> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }

    return this.get(`/api/project/${projectId}/access`)
  }

  /**
   * Invite a client to access a project
   */
  async inviteClient(inviteData: ProjectInviteData): Promise<ProjectAccessResponse | null> {
    if (!inviteData.projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }

    return this.post(`/api/project/${inviteData.projectId}/access/invite`, {
      clientName: inviteData.clientName,
      email: inviteData.email,
      accessRights: inviteData.accessRights
    })
  }

  /**
   * Revoke a client's access to a project
   */
  async revokeAccess(projectId: string, accessId: string): Promise<InvitationActionResponse | null> {
    if (!projectId || !accessId) {
      return Promise.reject({ message: 'Project ID and Access ID are required' })
    }

    return this.delete(`/api/project/${projectId}/access/${accessId}`)
  }

  /**
   * Resend OTP invitation for a client
   */
  async resendOtp(projectId: string, accessId: string): Promise<InvitationActionResponse | null> {
    if (!projectId || !accessId) {
      return Promise.reject({ message: 'Project ID and Access ID are required' })
    }

    return this.post(`/api/project/${projectId}/access/${accessId}/resend-otp`, {})
  }

  /**
   * Resend invitation for a client
   */
  async resendInvitation(projectId: string, accessId: string): Promise<InvitationActionResponse | null> {
    if (!projectId || !accessId) {
      return Promise.reject({ message: 'Project ID and Access ID are required' })
    }

    return this.post(`/api/project/${projectId}/access/${accessId}/resend`, {})
  }

  /**
   * Update a client's access rights
   */
  async updateAccess(
    projectId: string, 
    accessId: string, 
    updateData: ProjectAccessUpdateData
  ): Promise<ProjectAccessResponse | null> {
    if (!projectId || !accessId) {
      return Promise.reject({ message: 'Project ID and Access ID are required' })
    }

    return this.put(`/api/project/${projectId}/access/${accessId}`, updateData)
  }

  /**
   * Get a specific client's access details
   */
  async getAccessDetails(projectId: string, accessId: string): Promise<ProjectAccessResponse | null> {
    if (!projectId || !accessId) {
      return Promise.reject({ message: 'Project ID and Access ID are required' })
    }

    return this.get(`/api/project/${projectId}/access/${accessId}`)
  }

  /**
   * Get all pending invitations for the current user
   */
  async getUserPendingInvitations(): Promise<UserInvitationListResponse | null> {
    return this.get('/api/user/invitations/pending')
  }

  /**
   * Accept a pending invitation
   */
  async acceptInvitation(invitationId: string): Promise<InvitationActionResponse | null> {
    if (!invitationId) {
      return Promise.reject({ message: 'Invitation ID is required' })
    }

    return this.post(`/api/user/invitations/${invitationId}/accept`, {})
  }

  /**
   * Decline a pending invitation
   */
  async declineInvitation(invitationId: string): Promise<InvitationActionResponse | null> {
    if (!invitationId) {
      return Promise.reject({ message: 'Invitation ID is required' })
    }

    return this.post(`/api/user/invitations/${invitationId}/decline`, {})
  }
}

export default new ProjectAccessService()
